import React from 'react';
import { authFetch } from '../../utils/authFetch';
import { formatMessageText, getFileName } from '../../utils/messageHelpers';
import { Message } from '../../types/wa_messages';

interface TemplateMsgModalProps {
  templateModalOpen: boolean;
  setTemplateModalOpen: (open: boolean) => void;
  selectedTemplate: any | null;
  templateVars: { [key: string]: string };
  setTemplateVars: (vars: { [key: string]: string }) => void;
  selectedConversationId: string | null;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setToastMsg: (msg: string | null) => void;
  replyingTo: Message | null;
  user: any | null;
}

const TemplateMsgModal: React.FC<TemplateMsgModalProps> = ({
  templateModalOpen,
  setTemplateModalOpen,
  selectedTemplate,
  templateVars,
  setTemplateVars,
  selectedConversationId,
  accessToken,
  setUserAndToken,
  setMessages,
  setToastMsg,
  replyingTo,
  user,
}) => {
  if (!templateModalOpen || !selectedTemplate) return null;

  // Helper function to compose template text
  const composeTemplateText = () => {
    const parts: string[] = [];
    
    // Handle header and body text
    const textComponents = selectedTemplate.components.filter((c: any) => c.type === 'HEADER' || c.type === 'BODY');
    const textParts = textComponents.map((comp: any) => {
      let text = typeof comp.text === 'string' ? comp.text : '';
      if (text) {
        Object.entries(templateVars).forEach(([k, v]) => {
          text = text.replaceAll(`{{${k}}}`, v || '');
        });
      }
      return text;
    }).filter(Boolean);
    parts.push(...textParts);
    
    // Handle button components
    const buttonComponents = selectedTemplate.components.filter((c: any) => c.type === 'BUTTONS');
    buttonComponents.forEach((comp: any) => {
      if (comp.buttons) {
        comp.buttons.forEach((button: any, buttonIndex: number) => {
          if (button.type === 'URL') {
            let buttonText = `[${button.text}]`;
            if (button.url) {
              let url = button.url;
              // Replace URL parameters with user inputs
              Object.entries(templateVars).forEach(([k, v]) => {
                if (k.startsWith(`button_${buttonIndex}_param_`)) {
                  const paramNum = k.split('_').pop();
                  url = url.replace(`{{${paramNum}}}`, v || '');
                }
              });
              buttonText += ` → ${url}`;
            }
            parts.push(buttonText);
          }
        });
      }
    });
    
    return parts.join('\n\n');
  };

  // Helper function to get template preview
  const getTemplatePreview = () => {
    const preview = composeTemplateText();
    return preview ? formatMessageText(preview) : '[No preview]';
  };

  // Helper function to extract variables from template
  const extractTemplateVariables = () => {
    const allVars: string[] = [];
    
    // Extract variables from header and body text
    const texts = selectedTemplate.components.filter((c: any) => typeof c.text === 'string');
    const textVars = Array.from(
      texts.map((c: any) => c.text).join(' ').matchAll(/{{(.*?)}}/g)
    ).map(m => (m as RegExpMatchArray)[1]);
    allVars.push(...textVars);
    
    // Extract variables from button URLs
    selectedTemplate.components.forEach((comp: any) => {
      if (comp.type === 'BUTTONS' && comp.buttons) {
        comp.buttons.forEach((button: any, buttonIndex: number) => {
          if (button.type === 'URL' && button.url) {
            const urlMatches = Array.from(button.url.matchAll(/{{(\d+)}}/g));
            urlMatches.forEach(match => {
              const paramNum = (match as RegExpMatchArray)[1];
              const varName = `button_${buttonIndex}_param_${paramNum}`;
              allVars.push(varName);
            });
          }
        });
      }
    });
    
    return Array.from(new Set(allVars));
  };

  // Helper function to get example values for variables
  const getExampleMap = () => {
    const exampleMap: Record<string, string> = {};
    
    selectedTemplate.components.forEach((comp: any) => {
      if (comp.example) {
        // Handle header parameters
        if (comp.example.header_text_named_params && Array.isArray(comp.example.header_text_named_params)) {
          comp.example.header_text_named_params.forEach((param: any) => {
            if (param && param.param_name && param.example) {
              exampleMap[param.param_name] = param.example;
            }
          });
        }
        
        // Handle body parameters
        if (comp.example.body_text_named_params && Array.isArray(comp.example.body_text_named_params)) {
          comp.example.body_text_named_params.forEach((param: any) => {
            if (param && param.param_name && param.example) {
              exampleMap[param.param_name] = param.example;
            }
          });
        }
      }
      
      // Handle button examples
      if (comp.type === 'BUTTONS' && comp.buttons) {
        comp.buttons.forEach((button: any, buttonIndex: number) => {
          if (button.type === 'URL' && button.example && Array.isArray(button.example)) {
            const urlMatches = Array.from(button.url.matchAll(/{{(\d+)}}/g));
            urlMatches.forEach((match, paramIndex) => {
              const paramNum = (match as RegExpMatchArray)[1];
              const varName = `button_${buttonIndex}_param_${paramNum}`;
              if (button.example[paramIndex]) {
                exampleMap[varName] = button.example[paramIndex];
              }
            });
          }
        });
      }
    });
    
    return exampleMap;
  };

  // Handle template sending
  const handleSendTemplate = async () => {
    try {
      const composedText = composeTemplateText();
      
      // Send template message to backend
      const resp = await authFetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
        `/api/v1/admin/conversations/${selectedConversationId}/send_template_message`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            template_data: selectedTemplate,
            user_inputs: templateVars,
            composed_text: composedText
          })
        },
        accessToken,
        setUserAndToken
      );
      
      if (resp.ok) {
        const now = new Date().toISOString();
        const data = await resp.json();
        
        // Optimistically add the message to the UI
        setMessages(prev => [
          ...prev,
          {
            id: 'temp-' + now,
            conversation_id: selectedConversationId || '',
            direction: 'outbound',
            channel: 'whatsapp',
            message_type: 'template',
            content: composedText,
            media_urls: [],
            backup_media_urls: [],
            whatsapp_message_id: data.wa_api_response?.messages?.[0]?.id || '',
            timestamp: now,
            delivery_status: 'sent',
            admin_name: user?.name || undefined,
            processed_by: undefined,
            intent_detected: undefined,
            confidence_score: undefined,
            error_message: undefined,
            payload_json: {
              type: 'template',
              template: {
                name: selectedTemplate?.name,
                ...selectedTemplate
              }
            },
            quoted_wa_msg: replyingTo
              ? {
                  content: replyingTo.content,
                  message_type: replyingTo.message_type,
                  backup_media_urls: replyingTo.backup_media_urls,
                  filename: replyingTo.media_urls && replyingTo.media_urls.length > 0 ? getFileName(replyingTo.media_urls[0]) : undefined,
                }
              : undefined,
          } as Message
        ]);
        
        setTemplateModalOpen(false);
        setToastMsg("Template message sent!");
        setTimeout(() => setToastMsg(null), 3000);
        setTemplateVars({});
      } else {
        const error = await resp.json().catch(() => ({}));
        setToastMsg(`Failed to send template message: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending template message:', error);
      setToastMsg('Failed to send template message. Please try again.');
    }
  };

  const vars = extractTemplateVariables();
  const exampleMap = getExampleMap();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setTemplateModalOpen(false)}>
      <div className="bg-white rounded-xl shadow-lg p-6 min-w-[600px] max-w-3xl w-full relative" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-2">{selectedTemplate.name}</h2>
        
        {/* Combined header+body preview with live user input */}
        <div className="mb-4 text-slate-700">
          <div className="bg-slate-50 rounded p-4 text-slate-800 text-base whitespace-pre-line mb-2" style={{ fontFamily: 'inherit' }}>
            {getTemplatePreview()}
          </div>
        </div>
        
        {/* Variable inputs in 3 columns, with example as hint */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {vars.length === 0 ? (
            <div className="text-xs text-slate-400 col-span-3">No variables.</div>
          ) : (
            vars.map(v => {
              // Determine if this is a button variable
              const isButtonVar = v.startsWith('button_') && v.includes('_param_');
              const buttonInfo = isButtonVar ? v.split('_') : null;
              const buttonIndex = buttonInfo ? parseInt(buttonInfo[1]) : null;
              const paramNum = buttonInfo ? buttonInfo[3] : null;
              
              // Get button text for display
              let buttonText = '';
              if (isButtonVar && buttonIndex !== null) {
                const buttonComponent = selectedTemplate.components.find((c: any) => c.type === 'BUTTONS');
                if (buttonComponent && buttonComponent.buttons && buttonComponent.buttons[buttonIndex]) {
                  buttonText = buttonComponent.buttons[buttonIndex].text || `Button ${buttonIndex + 1}`;
                }
              }
              
              const displayLabel = isButtonVar 
                ? `${buttonText}`
                : v;
              
              return (
                <div key={v} className="mb-2 flex flex-col">
                  <label className="block text-sm text-slate-600 mb-1">{displayLabel}</label>
                  <input
                    className="w-full border border-slate-200 rounded px-2 py-1 text-sm"
                    value={templateVars[v] || ''}
                    onChange={e => setTemplateVars({ ...templateVars, [v]: e.target.value })}
                    placeholder={isButtonVar ? `Enter value for ${buttonText}` : `Enter value for ${v}`}
                  />
                  {exampleMap[v] && <span className="text-xs text-slate-500 mt-1">Eg: {exampleMap[v]}</span>}
                </div>
              );
            })
          )}
        </div>
        
        <div className="flex gap-2 justify-end">
          <button 
            className="px-4 py-2 rounded bg-slate-200 text-slate-700" 
            onClick={() => setTemplateModalOpen(false)}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 rounded bg-blue-600 text-white" 
            onClick={handleSendTemplate}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateMsgModal; 